/**
 * プロジェクトに追加するメールを選ぶピッカー。
 * 現在そのプロジェクトに未登録で、_deleted=false のメールのみ候補に出す。
 */
import { Mail as MailIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useMails, useUpdateMail } from '@/hooks/useMailData';
import { formatDate } from '@/lib/firestoreTime';

interface MailPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
}

export function MailPickerDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
}: MailPickerDialogProps) {
  const { data: allMails, isLoading } = useMails({ deleted: false, limit: 500 });
  const updateMutation = useUpdateMail();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [keyword, setKeyword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const candidates = useMemo(() => {
    const filtered = (allMails ?? []).filter((m) => !m.projectIds?.includes(projectId));
    if (!keyword.trim()) return filtered;
    const k = keyword.toLowerCase();
    return filtered.filter(
      (m) =>
        m.subject?.toLowerCase().includes(k) ||
        m.sections?.some((s) => s.content?.toLowerCase().includes(k)),
    );
  }, [allMails, projectId, keyword]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      const toAdd = Array.from(selected);
      await Promise.all(
        toAdd.map((id) => {
          const mail = allMails?.find((m) => m.id === id);
          const next = [...(mail?.projectIds ?? []), projectId];
          return updateMutation.mutateAsync({ id, data: { projectIds: next } });
        }),
      );
      toast.success(`${toAdd.length} 件を追加しました`);
      setSelected(new Set());
      setKeyword('');
      onOpenChange(false);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>メールをプロジェクトに追加</DialogTitle>
          <DialogDescription>
            「{projectName}」に追加するメールを選択してください
          </DialogDescription>
        </DialogHeader>

        <Input
          placeholder="件名 / 本文で検索..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />

        <div className="flex-1 overflow-y-auto rounded border border-[var(--border)] min-h-[200px]">
          {isLoading ? (
            <div className="p-8">
              <Spinner />
            </div>
          ) : candidates.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--text-muted)]">
              追加可能なメールがありません
            </div>
          ) : (
            candidates.map((m) => (
              <label
                key={m.id}
                className="flex items-center gap-3 p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.has(m.id)}
                  onChange={() => toggle(m.id)}
                  className="size-4"
                />
                <MailIcon className="size-4 text-[var(--text-muted)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium truncate">
                      {m.subject || '(件名なし)'}
                    </span>
                    <Badge variant="default">{m.status}</Badge>
                  </div>
                  <div className="text-xs text-[var(--text-muted)]">{formatDate(m.createdAt)}</div>
                </div>
              </label>
            ))
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleAdd}
            disabled={submitting || selected.size === 0}
          >
            {submitting ? '追加中...' : `${selected.size} 件を追加`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
